import { generateHTML, generateJSON } from "@tiptap/html";
import type { Extensions, JSONContent } from "@tiptap/core";

/** HTML 문자열 → ProseMirror 도큐먼트(JSON). 프리셋 스키마 외 마크업은 정규화·제거. */
export function htmlToDoc(html: string, extensions: Extensions): JSONContent {
  return generateJSON(html, extensions) as JSONContent;
}

/** ProseMirror 도큐먼트(JSON) → HTML 문자열. */
export function docToHtml(doc: JSONContent, extensions: Extensions): string {
  return generateHTML(doc, extensions);
}

/** HTML을 프리셋 스키마로 round-trip 정규화(저장 전 표준화). */
export function normalizeHtml(html: string, extensions: Extensions): string {
  return docToHtml(htmlToDoc(html, extensions), extensions);
}

// 인라인 요소: 부모 줄에 그대로 유지(텍스트 흐름 보존). 그 외 요소는 블록으로 줄 분리.
const INLINE_TAGS = new Set([
  "a", "abbr", "b", "bdi", "bdo", "br", "cite", "code", "data", "dfn", "em",
  "i", "img", "kbd", "mark", "q", "s", "samp", "small", "span", "strong",
  "sub", "sup", "time", "u", "var", "wbr",
]);
// 닫는 태그가 없는 void 요소.
const VOID_TAGS = new Set([
  "area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta",
  "param", "source", "track", "wbr",
]);

const escapeText = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * 저장형 HTML을 블록 단위로 들여쓰기해 보기 좋게 정렬(HTML 편집 모드 textarea 표시용).
 * - 인라인 요소(span/strong/a/img 등)와 텍스트는 부모 줄에 유지해 흐름을 보존한다.
 * - 블록 요소는 줄을 나누고 2칸 들여쓴다.
 * DOM 파싱 round-trip이라 태그/속성은 브라우저 표준형으로 정규화되지만, 공백 외
 * 의미 변화는 없다(렌더 결과 동일). DOM이 없는 환경에선 원본을 그대로 반환한다.
 */
export function formatHtml(html: string): string {
  if (typeof document === "undefined") return html;
  const root = document.createElement("div");
  root.innerHTML = html;

  const lines: string[] = [];
  const pad = (depth: number): string => "  ".repeat(depth);
  // 자식 없는 얕은 복제로 여는 태그만 직렬화(<p class="x"></p> → <p class="x">).
  const openTag = (el: Element): string =>
    (el.cloneNode(false) as Element).outerHTML.replace(/<\/[a-z0-9-]+>$/i, "");
  const isInline = (node: Node): boolean =>
    node.nodeType === 3 ||
    (node.nodeType === 1 && INLINE_TAGS.has((node as Element).tagName.toLowerCase()));

  const walk = (parent: Node, depth: number): void => {
    parent.childNodes.forEach((node) => {
      if (node.nodeType === 3) {
        const text = (node.textContent ?? "").replace(/\s+/g, " ").trim();
        if (text) lines.push(pad(depth) + escapeText(text));
        return;
      }
      if (node.nodeType !== 1) return;
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      if (VOID_TAGS.has(tag)) {
        lines.push(pad(depth) + openTag(el));
        return;
      }
      // 자식이 전부 인라인이면 한 줄로(<p>본문 <strong>강조</strong></p>).
      if (Array.from(el.childNodes).every(isInline)) {
        lines.push(pad(depth) + openTag(el) + el.innerHTML.trim() + `</${tag}>`);
        return;
      }
      lines.push(pad(depth) + openTag(el));
      walk(el, depth + 1);
      lines.push(pad(depth) + `</${tag}>`);
    });
  };

  walk(root, 0);
  return lines.join("\n");
}
